import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Download, Mail, Cookie, Trash2, FileText, ShieldCheck, Loader2, AlertTriangle, KeyRound, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { translateError } from "../utils/i18nErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared privacy/settings block — rendered inside both the candidate dashboard
// ("Privacidad" tab) and the employer Company tab, right below Plan y Facturación.
export function PrivacySettingsPanel() {
  const { t } = useLanguage();
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const [hasPassword, setHasPassword] = useState(true);
  const [marketingOptIn, setMarketingOptIn] = useState(!!user?.marketing_email_opt_in);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPwdFields, setShowPwdFields] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState("");
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: getAuthHeaders() })
      .then((res) => { setHasPassword(!!res.data.has_password); setMarketingOptIn(!!res.data.marketing_email_opt_in); })
      .catch(() => {});
  }, []);

  const toggleMarketing = async (checked) => {
    setMarketingOptIn(checked);
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/users/me/preferences`, { marketing_email_opt_in: checked }, { headers: getAuthHeaders() });
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/users/me/export`, { headers: getAuthHeaders() });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "infocityjobs-my-data.json";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleteError("");
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/me`, {
        headers: getAuthHeaders(),
        data: hasPassword ? { password: deletePassword } : { confirm: deleteConfirmText.trim().toLowerCase() === t("privacy.deleteConfirmWord").toLowerCase() },
      });
      logout();
      navigate("/");
    } catch (err) {
      setDeleteError(err.response?.data?.detail === "Incorrect password" ? t("privacy.incorrectPassword") : t("privacy.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordError("");
    if (newPassword.length < 8) { setChangePasswordError(t("auth.passwordTooShort")); return; }
    if (newPassword !== confirmNewPassword) { setChangePasswordError(t("auth.passwordMismatch")); return; }
    setChangingPassword(true);
    try {
      const res = await axios.post(`${API}/users/me/change-password`, { current_password: currentPassword, new_password: newPassword }, { headers: getAuthHeaders() });
      login(res.data.token, user); // fresh token for this device — other sessions get logged out
      setChangePasswordSuccess(true);
      setCurrentPassword(""); setNewPassword(""); setConfirmNewPassword("");
    } catch (err) {
      setChangePasswordError(translateError(err.response?.data?.detail || "", t) || t("errors.unknownError"));
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div data-testid="privacy-settings-panel" className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-5">
        <ShieldCheck className="h-5 w-5 text-[#028CF8]" />
        <h3 className="font-poppins font-bold text-base text-slate-900">{t("privacy.title")}</h3>
      </div>

      <div className="space-y-4">
        <button onClick={handleExport} disabled={exporting} data-testid="export-my-data-button"
          className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors text-left disabled:opacity-60">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : <Download className="h-4 w-4 text-slate-500" />}
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-800">{t("privacy.exportData")}</p>
            <p className="text-xs text-slate-600">{t("privacy.exportDataHint")}</p>
          </div>
        </button>

        <label className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer">
          <Mail className="h-4 w-4 text-slate-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-800">{t("privacy.marketingEmails")}</p>
            <p className="text-xs text-slate-600">{t("privacy.marketingEmailsHint")}</p>
          </div>
          <input type="checkbox" data-testid="marketing-preference-toggle" checked={marketingOptIn} disabled={savingPrefs}
            onChange={(e) => toggleMarketing(e.target.checked)} className="h-4 w-4 accent-[#028CF8] flex-shrink-0" />
        </label>

        <button onClick={() => window.dispatchEvent(new CustomEvent("open-cookie-preferences"))} data-testid="open-cookie-preferences-button"
          className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors text-left">
          <Cookie className="h-4 w-4 text-slate-500" />
          <p className="text-sm font-medium text-slate-800 flex-1">{t("cookies.preferences")}</p>
        </button>

        <div className="flex gap-3">
          <a href="/terms" target="_blank" rel="noreferrer" data-testid="privacy-panel-terms-link" className="flex-1 flex items-center gap-2 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors text-sm text-slate-600">
            <FileText className="h-4 w-4 text-slate-400" /> {t("auth.termsOfService")}
          </a>
          <a href="/privacy" target="_blank" rel="noreferrer" data-testid="privacy-panel-privacy-link" className="flex-1 flex items-center gap-2 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors text-sm text-slate-600">
            <FileText className="h-4 w-4 text-slate-400" /> {t("auth.privacyPolicy")}
          </a>
        </div>

        {hasPassword && (
          <div className="border-t border-slate-100 pt-4">
            {!showChangePassword ? (
              <button onClick={() => setShowChangePassword(true)} data-testid="change-password-open-button"
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors text-left">
                <KeyRound className="h-4 w-4 text-slate-500" />
                <p className="text-sm font-medium text-slate-800 flex-1">{t("privacy.changePasswordTitle")}</p>
              </button>
            ) : (
              <div className="p-4 rounded-xl border border-slate-200 space-y-3" data-testid="change-password-box">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-[#028CF8]" />
                  <p className="text-sm font-bold text-slate-900">{t("privacy.changePasswordTitle")}</p>
                </div>
                {changePasswordSuccess ? (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-100">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-green-700 leading-relaxed" data-testid="change-password-success-message">{t("privacy.passwordChangedSuccess")}</p>
                  </div>
                ) : (
                  <form onSubmit={handleChangePassword} data-testid="change-password-form" className="space-y-2.5">
                    <p className="text-xs text-slate-500 leading-relaxed">{t("privacy.changePasswordHint")}</p>
                    <input type="password" data-testid="change-password-current-input" placeholder={t("privacy.currentPasswordLabel")}
                      value={currentPassword} onChange={(e) => { setCurrentPassword(e.target.value); setChangePasswordError(""); }} required
                      className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" />
                    <div className="relative">
                      <input type={showPwdFields ? "text" : "password"} data-testid="change-password-new-input" placeholder={t("auth.newPassword")}
                        value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setChangePasswordError(""); }} required minLength={8}
                        className="w-full h-10 rounded-lg border border-slate-200 px-3 pr-10 text-sm" />
                      <button type="button" onClick={() => setShowPwdFields(!showPwdFields)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPwdFields ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <input type={showPwdFields ? "text" : "password"} data-testid="change-password-confirm-input" placeholder={t("auth.confirmPassword")}
                      value={confirmNewPassword} onChange={(e) => { setConfirmNewPassword(e.target.value); setChangePasswordError(""); }} required minLength={8}
                      className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" />
                    {changePasswordError && <p className="text-xs text-red-600" data-testid="change-password-error">{changePasswordError}</p>}
                    <div className="flex gap-2 pt-1">
                      <button type="button" onClick={() => { setShowChangePassword(false); setChangePasswordError(""); setCurrentPassword(""); setNewPassword(""); setConfirmNewPassword(""); }}
                        data-testid="change-password-cancel-button" className="flex-1 h-10 rounded-lg border border-slate-200 text-sm text-slate-600">{t("common.cancel")}</button>
                      <button type="submit" disabled={changingPassword} data-testid="change-password-submit-button"
                        className="flex-1 h-10 rounded-lg bg-[#028CF8] text-white text-sm font-semibold disabled:opacity-60">
                        {changingPassword ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t("privacy.changePasswordButton")}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        )}

        <div className="border-t border-slate-100 pt-4">
          {!showDelete ? (
            <button onClick={() => setShowDelete(true)} data-testid="delete-account-open-button"
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-red-100 text-red-600 hover:bg-red-50 transition-colors text-left">
              <Trash2 className="h-4 w-4" />
              <p className="text-sm font-medium flex-1">{t("privacy.deleteAccount")}</p>
            </button>
          ) : (
            <div className="p-4 rounded-xl border border-red-200 bg-red-50/50 space-y-3" data-testid="delete-account-confirm-box">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700 leading-relaxed">{t("privacy.deleteWarning")}</p>
              </div>
              {hasPassword ? (
                <input type="password" data-testid="delete-account-password-input" placeholder={t("auth.passwordPlaceholder")}
                  value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" />
              ) : (
                <input type="text" data-testid="delete-account-confirm-input"
                  placeholder={t("privacy.deleteConfirmWord")}
                  value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" />
              )}
              {deleteError && <p className="text-xs text-red-600" data-testid="delete-account-error">{deleteError}</p>}
              <div className="flex gap-2">
                <button onClick={() => setShowDelete(false)} data-testid="delete-account-cancel-button"
                  className="flex-1 h-10 rounded-lg border border-slate-200 text-sm text-slate-600">{t("common.cancel")}</button>
                <button onClick={handleDelete} disabled={deleting} data-testid="delete-account-confirm-button"
                  className="flex-1 h-10 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-60">
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t("privacy.deleteAccount")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
